import { Button } from "@bcc/components/ui/button";
import { useCreateShippingDocuments } from "@bcc/hooks/use-create-shipping-documents";
import { useToast } from "@bcc/hooks/use-toast";
import { usePurchaseOrdersState } from "@client-shared/providers/purchase-orders";
import type { LabelPrintBatchDetail, PurchaseOrder, ServiceId, ShippingDocumentsTabType } from "@shared/types";
import { FilePlus, FileText, Loader2, Printer } from "lucide-react";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { SHIPPING_DOC_CREATE_STATUSES, SHIPPING_DOC_DISABLING_BATCH_STATUSES, SHIPPING_DOC_DISABLING_ORDER_STATUSES } from "../constants";
import { setOrderDetailShippingButtonsTestId } from "./order-detail-shipping-buttons.utils";

interface OrderDetailShippingButtonsProps {
  purchaseOrder: PurchaseOrder;
  batch?: LabelPrintBatchDetail;
  onOpenShippingDocuments?: (tab: ShippingDocumentsTabType) => void;
  testId?: string;
  vendorLink?: string | null;
  vendorLinkTestId?: string;
  vendorServiceId?: ServiceId;
}

export function OrderDetailShippingButtons({
  purchaseOrder,
  batch,
  onOpenShippingDocuments,
  testId,
  vendorLink,
  vendorLinkTestId,
  vendorServiceId,
}: OrderDetailShippingButtonsProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [state, , utils] = usePurchaseOrdersState();
  const { isCreating, handleCreate } = useCreateShippingDocuments();

  const rootTestId = testId ?? setOrderDetailShippingButtonsTestId("container");
  const { orderStatus, beanzConnectLabelPrintEnabled } = purchaseOrder;
  const isLabelLoading = Boolean(state.loadingKeys?.["getPurchaseOrderLabelUrl"]);
  const isPackingSlipLoading = Boolean(state.loadingKeys?.["getPurchaseOrderPackingSlipUrl"]);

  const isBaseDisabled = useMemo(
    () =>
      (orderStatus && SHIPPING_DOC_DISABLING_ORDER_STATUSES.has(orderStatus)) ||
      SHIPPING_DOC_DISABLING_BATCH_STATUSES.has(batch?.batchStatus ?? ("" as never)),
    [orderStatus, batch?.batchStatus]
  );

  const handleOpenDocument = useCallback(
    async (fileType: "Label" | "PackingSlip"): Promise<void> => {
      const url = await utils.getPurchaseOrderShippingDocumentUrl(purchaseOrder, fileType);
      if (!url) {
        toast({
          title: t("orders.orderDetail.documentFetchError"),
          variant: "destructive",
        });
        return;
      }
      window.open(url, "_blank", "noopener,noreferrer");
    },
    [utils, purchaseOrder, toast, t]
  );

  if (beanzConnectLabelPrintEnabled && orderStatus && SHIPPING_DOC_CREATE_STATUSES.has(orderStatus)) {
    return (
      <div className="flex gap-2" data-testid={rootTestId}>
        <Button
          size="lg"
          className="flex-1"
          disabled={isBaseDisabled || isCreating}
          onClick={() => void handleCreate(purchaseOrder)}
          testId={setOrderDetailShippingButtonsTestId("createButton")}
        >
          {isCreating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FilePlus className="mr-2 h-4 w-4" />}
          {t("orders.orderDetail.createShippingDocuments")}
        </Button>
      </div>
    );
  }

  const isDefaultDisabled = isBaseDisabled || orderStatus === "LabelError";

  return (
    <div className="flex gap-2" data-testid={rootTestId}>
      {beanzConnectLabelPrintEnabled && (
        <Button
          size="lg"
          className="flex-1"
          disabled={isDefaultDisabled || (!onOpenShippingDocuments && isLabelLoading)}
          onClick={onOpenShippingDocuments ? () => onOpenShippingDocuments("shipping-label") : () => void handleOpenDocument("Label")}
          testId={setOrderDetailShippingButtonsTestId("printLabelButton")}
        >
          <Printer className="mr-2 h-4 w-4" />
          {t("buttons.shippingLabels")}
        </Button>
      )}
      <Button
        size="lg"
        variant="outline"
        className="flex-1 bg-transparent"
        disabled={isDefaultDisabled || (!onOpenShippingDocuments && isPackingSlipLoading)}
        onClick={onOpenShippingDocuments ? () => onOpenShippingDocuments("packing-slip") : () => void handleOpenDocument("PackingSlip")}
        testId={setOrderDetailShippingButtonsTestId("packingSlipButton")}
      >
        <FileText className="mr-2 h-4 w-4" />
        {t("buttons.packingSlip")}
      </Button>
      {vendorLink && (
        <Button size="lg" variant="outline" className="flex-1 bg-transparent" asChild testId={vendorLinkTestId}>
          <a href={vendorLink} target="_blank" rel="noopener noreferrer">
            <img
              src={vendorServiceId === "woocommerce" ? "/images/woocommerce-logo.svg" : "/images/shopify-logo.svg"}
              alt=""
              className="mr-2 h-4 w-4"
            />
            {vendorServiceId === "woocommerce" ? t("orders.orderDetail.viewInWooCommerce") : t("orders.orderDetail.viewInShopify")}
          </a>
        </Button>
      )}
    </div>
  );
}
